import React, { useState } from 'react';
import lightBulbImage from 'images/categories/lightbulb.svg';
import rocketImage from 'images/categories/rocket.svg';
import cloverImage from 'images/categories/clover.svg';
import rainbowImage from 'images/categories/rainbow.svg';
import starImage from 'images/categories/star.svg';
import flowerImage from 'images/categories/flower.svg';
import {
  CATEGORY_INNOVATION,
  CATEGORY_SUSTAINABLE,
  CATEGORY_DIGITAL_TRANSFORMATION,
  CATEGORY_AI,
  CATEGORY_YOUNG_LEADER,
  CATEGORY_WOMEN_IN_TECH,
  CATEGORY_NEW_BUSINESS_VENTURE,
  CATEGORY_WOMEN_IN_TECH_2026,
  CATEGORY_DIGITAL_CATALYST,
} from 'constants/evaluation';

// Local icon paths from Figma exports
const iconImages: Record<string, string> = {
  lightbulb: lightBulbImage,
  rocket: rocketImage,
  clover: cloverImage,
  rainbow: rainbowImage,
  star: starImage,
  flower: flowerImage,
};

interface AwardCategory {
  title: string;
  description: string;
  icon: string;
  category: 'organizations' | 'individual';
}

interface YearCategories {
  organizations: AwardCategory[];
  individual: AwardCategory[];
}

const categoriesByYear: Record<number, YearCategories> = {
  2023: {
    organizations: [
      {
        title: 'Exponential Growth',
        description:
          'Recognizing projects that achieved significant and rapid business growth through digital initiatives.',
        icon: iconImages.lightbulb,
        category: 'organizations',
      },
      {
        title: 'Efficiency Excellence',
        description:
          'Honoring initiatives that significantly improved operational efficiency and resource optimization.',
        icon: iconImages.rocket,
        category: 'organizations',
      },
      {
        title: 'Disruptive Technology',
        description:
          'Celebrating the use of cutting-edge technologies that challenge traditional business models.',
        icon: iconImages.rainbow,
        category: 'organizations',
      },
    ],
    individual: [],
  },
  2024: {
    organizations: [
      {
        title: 'Innovation Excellence',
        description:
          'Recognizing groundbreaking digital innovations that drive business transformation.',
        icon: iconImages.lightbulb,
        category: 'organizations',
      },
      {
        title: 'Digital Transformation Champion',
        description:
          'Honoring large-scale digital transformation projects that successfully lead change.',
        icon: iconImages.rocket,
        category: 'organizations',
      },
      {
        title: 'Sustainable Solution',
        description:
          'Celebrating solutions that promote environmental sustainability and social responsibility.',
        icon: iconImages.clover,
        category: 'organizations',
      },
      {
        title: 'Best Collaborative Project',
        description:
          'Recognizing the power of collaboration between different units or partners to achieve a common goal.',
        icon: iconImages.rainbow,
        category: 'organizations',
      },
    ],
    individual: [
      {
        title: 'Young Digital Leaders',
        description:
          'Recognizing outstanding young professionals making significant contributions to digital initiatives.',
        icon: iconImages.star,
        category: 'individual',
      },
    ],
  },
  2025: {
    organizations: [
      {
        title: CATEGORY_INNOVATION,
        description:
          'Leveraging partners collaboration to boost innovation, efficiency and position your business as an industry leader.',
        icon: iconImages.lightbulb,
        category: 'organizations',
      },
      {
        title: CATEGORY_SUSTAINABLE,
        description:
          'Honoring digital initiatives that drive environmental sustainability and social impact.',
        icon: iconImages.clover,
        category: 'organizations',
      },
      {
        title: CATEGORY_DIGITAL_TRANSFORMATION,
        description:
          'Celebrating teams that successfully lead large-scale digital transformation projects.',
        icon: iconImages.rocket,
        category: 'organizations',
      },
      {
        title: CATEGORY_AI,
        description:
          'Recognizing innovative applications of artificial intelligence and machine learning.',
        icon: iconImages.rainbow,
        category: 'organizations',
      },
    ],
    individual: [
      {
        title: CATEGORY_YOUNG_LEADER,
        description:
          'Honoring emerging talent under 35 who demonstrate exceptional digital leadership.',
        icon: iconImages.star,
        category: 'individual',
      },
      {
        title: CATEGORY_WOMEN_IN_TECH,
        description:
          'Celebrating women driving innovation and excellence in technology fields.',
        icon: iconImages.flower,
        category: 'individual',
      },
    ],
  },
  2026: {
    organizations: [
      {
        title: CATEGORY_DIGITAL_TRANSFORMATION,
        description:
          'Celebrating teams that successfully lead large-scale digital transformation projects.',
        icon: iconImages.rocket,
        category: 'organizations',
      },
      {
        title: CATEGORY_SUSTAINABLE,
        description:
          'Honoring digital initiatives that drive environmental sustainability and social impact.',
        icon: iconImages.clover,
        category: 'organizations',
      },
      {
        title: CATEGORY_AI,
        description:
          'Recognizing innovative applications of artificial intelligence and machine learning.',
        icon: iconImages.rainbow,
        category: 'organizations',
      },
      {
        title: CATEGORY_NEW_BUSINESS_VENTURE,
        description:
          'Celebrating new business ventures that demonstrate strong commercial traction, innovative technology, and high scalability.',
        icon: iconImages.lightbulb,
        category: 'organizations',
      },
    ],
    individual: [
      {
        title: CATEGORY_WOMEN_IN_TECH_2026,
        description:
          'Celebrating women driving innovation, technology leadership, business impact, and mentorship.',
        icon: iconImages.flower,
        category: 'individual',
      },
      {
        title: CATEGORY_DIGITAL_CATALYST,
        description:
          'Honoring outstanding individuals who drive transformation, demonstrate execution leadership, and influence beyond their scope.',
        icon: iconImages.star,
        category: 'individual',
      },
    ],
  },
};

const AwardCategoriesSection: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState<number>(2026);
  const years = [2023, 2024, 2025, 2026];

  const currentCategories = categoriesByYear[selectedYear] || {
    organizations: [],
    individual: [],
  };

  return (
    <section
      id="categories"
      className="py-16 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bai font-bold mb-4">
            Awards Categories
          </h2>
          <p className="max-w-2xl mx-auto text-lg">
            Each year, we introduce awards categories that reflect the evolving
            digital landscape and emerging priorities across Sinar Mas Group.
          </p>
        </div>

        <div className="text-center mb-12">
          {/* Year Selector */}
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-8 sm:mb-12">
            {years.map(year => (
              <button
                key={year}
                onClick={() => {
                  setSelectedYear(year);
                }}
                className={`cursor-pointer px-5 sm:px-8 py-2 rounded-full font-semibold transition-colors text-sm sm:text-base ${
                  selectedYear === year
                    ? 'bg-smdd-red text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {year}
              </button>
            ))}
          </div>
        </div>

        <>
          {/* Organizations / Teams Section */}
          {currentCategories.organizations.length > 0 && (
            <div className="mb-16">
              <div className="flex justify-center mb-8">
                <span className="px-6 py-2 rounded-full border-2 border-smdd-red text-smdd-red bg-white font-medium text-sm">
                  ORGANIZATIONS / TEAMS
                </span>
              </div>
              <div className="flex flex-wrap justify-center gap-x-8 gap-y-12">
                {currentCategories.organizations.map((award, index) => (
                  <div
                    key={index}
                    className="flex flex-col items-center text-center w-[calc(50%-1rem)] lg:w-[calc(25%-2rem)] max-w-[280px]"
                  >
                    {/* Hexagon Card - Using exported SVG */}
                    <div className="relative w-full flex justify-center mb-4">
                      <img
                        src={award.icon}
                        alt={award.title}
                        width={307}
                        height={362}
                        className="w-36 h-36 object-contain"
                      />
                    </div>

                    {/* Award Info */}
                    <h3 className="text-lg font-bold mb-2 text-smdd-red">
                      {award.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {award.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Individual Section */}
          {currentCategories.individual.length > 0 && (
            <div>
              <div className="flex justify-center mb-8">
                <span className="px-6 py-2 rounded-full border-2 border-blue-500 text-blue-600 bg-white font-medium text-sm">
                  INDIVIDUAL
                </span>
              </div>
              <div className="flex flex-wrap justify-center gap-x-16 gap-y-12 max-w-4xl mx-auto">
                {currentCategories.individual.map((award, index) => (
                  <div
                    key={index}
                    className="flex flex-col items-center text-center w-[calc(50%-2rem)] md:w-[280px]"
                  >
                    {/* Hexagon Card - Using exported SVG */}
                    <div className="relative w-full flex justify-center mb-4">
                      <img
                        src={award.icon}
                        alt={award.title}
                        width={307}
                        height={362}
                        className="w-36 h-36 object-contain"
                      />
                    </div>

                    {/* Award Info */}
                    <h3 className="text-lg font-bold mb-2 text-blue-600">
                      {award.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {award.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      </div>
    </section>
  );
};

export default AwardCategoriesSection;
